import { useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "components/DesignSystem/Button/Button";
import { CrossOutlined } from "components/icons/CrossOutlined";
import { PlayIcon } from "components/icons/PlayIcon";
import { ProductTourPoster } from "components/icons/ProductTourPoster";
import {
  PLATFORM_TOUR_LAYOUT_DURATION_S,
  PLATFORM_TOUR_LAYOUT_ID,
  PLATFORM_TOUR_LAYOUT_TRANSITION,
} from "constants/productTour";

type PlatformTourWidgetProps = {
  open: boolean;
  isMorphing: boolean;
  landingVideo: HTMLVideoElement | null;
  onOpenPlayer: () => void;
  onDismiss: () => void;
};

export const PlatformTourWidget = ({
  open,
  isMorphing,
  landingVideo,
  onOpenPlayer,
  onDismiss,
}: PlatformTourWidgetProps) => {
  const chromeInitial = isMorphing ? { opacity: 0 } : false;
  const chromeAnimate = {
    opacity: 1,
    transition: {
      delay: PLATFORM_TOUR_LAYOUT_DURATION_S * 0.8,
      duration: PLATFORM_TOUR_LAYOUT_DURATION_S * 0.2,
    },
  };
  const isPosterMorph = isMorphing && !landingVideo;
  const mediaLayoutId = isMorphing ? PLATFORM_TOUR_LAYOUT_ID.MEDIA : undefined;

  const drawLandingFrame = useCallback(
    (canvas: HTMLCanvasElement | null) => {
      if (!canvas || !landingVideo) return;

      canvas.width = landingVideo.videoWidth || landingVideo.clientWidth;
      canvas.height = landingVideo.videoHeight || landingVideo.clientHeight;
      try {
        canvas
          .getContext("2d")
          ?.drawImage(landingVideo, 0, 0, canvas.width, canvas.height);
      } catch (error) {
        console.error("Could not capture the video frame", error);
      }
    },
    [landingVideo]
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={isMorphing ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
          className="t-fixed t-bottom-6 t-right-6 t-z-overlay t-w-[400px] sm:t-left-3 sm:t-right-3 sm:t-w-auto"
        >
          <motion.div
            style={{ borderRadius: 8 }}
            className="t-group t-relative"
          >
            <motion.div
              aria-hidden
              initial={chromeInitial}
              animate={chromeAnimate}
              className="t-absolute t-inset-0 t-rounded-lg t-border t-border-solid t-border-purple-20 t-bg-purple-0 t-shadow-l"
            />
            <Button
              customType="ghost"
              onClick={onOpenPlayer}
              aria-label="Watch the platform tour, 90 seconds"
              className="!t-h-auto !t-w-full !t-justify-start !t-gap-3 !t-whitespace-normal !t-py-2.5 !t-pl-2.5 !t-pr-12 !t-text-left hover:!t-bg-surface-transparent"
            >
              <motion.div
                layoutId={mediaLayoutId}
                layoutCrossfade={false}
                transition={PLATFORM_TOUR_LAYOUT_TRANSITION}
                style={{ borderRadius: 6 }}
                className="t-relative t-aspect-video t-w-20 t-shrink-0 t-overflow-hidden"
              >
                <ProductTourPoster
                  className="t-block t-size-full"
                  preserveAspectRatio="xMinYMid slice"
                />
                {landingVideo && (
                  <canvas
                    ref={drawLandingFrame}
                    className="t-absolute t-inset-0 t-size-full t-object-cover"
                  />
                )}
                <motion.div
                  initial={isPosterMorph ? { opacity: 0 } : false}
                  animate={chromeAnimate}
                  className="t-absolute t-inset-0 t-flex t-items-center t-justify-center t-bg-text-100/30 t-text-white t-transition-colors group-hover:t-bg-text-100/40"
                >
                  <PlayIcon
                    width={20}
                    height={20}
                    className="t-transition-transform group-hover:t-scale-110"
                  />
                </motion.div>
              </motion.div>
              <motion.div
                layout="position"
                initial={isMorphing ? { opacity: 0, x: -8 } : false}
                animate={{
                  opacity: 1,
                  x: 0,
                  transition: { delay: PLATFORM_TOUR_LAYOUT_DURATION_S * 0.7 },
                }}
                className="t-flex t-min-w-0 t-flex-col t-gap-0.5 t-pl-2"
              >
                <span className="t-truncate t-text-subtitle t-text-text-100">
                  See how Inkle Tax works
                </span>
                <span className="t-text-body-sm-italic t-italic t-text-text-30">
                  Watch the 90-second platform tour
                </span>
              </motion.div>
            </Button>
            <motion.div
              initial={chromeInitial}
              animate={chromeAnimate}
              className="t-absolute t-inset-y-0 t-right-2.5 t-flex t-items-center"
            >
              <Button customType="ghost_icon" size="xsmall" onClick={onDismiss}>
                <CrossOutlined size="14" />
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
